import { IOverlayParameter, IPointGeometry } from "@/types/map";
import { loadModules } from "esri-loader";

export default class DiamondLayer {
  private static intances: Map<string, DiamondLayer>;

  public static getInstance(view: __esri.SceneView) {
    const id = view.container.id;
    if (!DiamondLayer.intances) {
      DiamondLayer.intances = new Map();
    }
    let instance = DiamondLayer.intances.get(id);
    if (!instance) {
      instance = new DiamondLayer(view);
      DiamondLayer.intances.set(id, instance);
    }
    return instance;
  }

  private view!: __esri.SceneView;
  private animateFeatureLayre!: __esri.FeatureLayer;
  private labelFeatureLayer!: __esri.FeatureLayer;
  private Color!: __esri.ColorConstructor;
  private Graphic!: __esri.GraphicConstructor;

  private animateSpeed = 0.03;
  private readonly defaultSymbolWidth = 50;
  private symbolWidth: number = this.defaultSymbolWidth;
  private readonly defaultSymbolHeight = 150;
  private symbolHeight = 300;
  private pointHeight = 0;
  private readonly pointMinHeight = 1.5;
  private readonly pointMaxHeight = 2.5;
  private symbolHeading = 1;
  private symbolStartColor!: __esri.Color;
  private symbolEndColor!: __esri.Color;
  private animating = false;

  private isAscending = true;

  private constructor(view: __esri.SceneView) {
    this.view = view;
  }

  public async initialize(param?: {
    symbolWidth?: number;
    symbolHeight?: number;
    speed?: number;
  }) {
    this.symbolWidth = param?.symbolWidth ?? this.defaultSymbolWidth;
    this.symbolHeight = param?.symbolHeight ?? this.defaultSymbolHeight;
    this.animateSpeed = param?.speed ?? 0.05;
    this.pointHeight = this.pointMinHeight;

    if (!this.animateFeatureLayre) {
      type MapModules = [
        typeof import("esri/layers/FeatureLayer"),
        typeof import("esri/Graphic"),
        typeof import("esri/Color")
      ];
      const [FeatureLayer, Graphic, Color] = await (loadModules([
        "esri/layers/FeatureLayer",
        "esri/Graphic",
        "esri/Color",
      ]) as Promise<MapModules>);

      this.Graphic = Graphic;
      this.Color = Color;
      this.symbolStartColor = new this.Color("#76ff03");
      this.symbolEndColor = new this.Color("#ccff90");
      // this.symbolEndColor.a = 0.7;

      this.animateFeatureLayre = new FeatureLayer({
        source: [],
        objectIdField: "OBJECTID",
        geometryType: "point",
        outFields: ["*"],
        spatialReference: { wkid: 4326 },
        hasZ: true,
        fields: [
          { name: "OBJECTID", type: "oid" },
          { name: "name", type: "string" },
          { name: "type", type: "string" },
          { name: "id", type: "string" },
        ],
        renderer: {
          type: "simple",
          symbol: {
            type: "point-3d",
            symbolLayers: [
              {
                type: "object",
                width: 5,
                height: (this.pointMaxHeight - 0.5) * this.symbolHeight,
                depth: 5,
                resource: { primitive: "cylinder" },
                material: { color: [255, 255, 255, 0.8] },
              },
              {
                type: "object",
                width: this.symbolWidth,
                height: this.symbolHeight,
                depth: this.symbolWidth,
                anchor: "relative",
                anchorPosition: {
                  x: 0,
                  y: 0,
                  z: this.pointHeight,
                },
                tilt: 180,
                heading: 0,
                resource: { primitive: "diamond" },
                material: { color: this.symbolStartColor },
              },
            ],
          },
        } as any,
      });
      this.view.map.add(this.animateFeatureLayre);

      this.labelFeatureLayer = new FeatureLayer({
        source: [],
        objectIdField: "OBJECTID",
        geometryType: "point",
        outFields: ["*"],
        spatialReference: { wkid: 4326 },
        hasZ: true,
        fields: [
          { name: "OBJECTID", type: "oid" },
          { name: "name", type: "string" },
          { name: "type", type: "string" },
          { name: "id", type: "string" },
        ],
        renderer: {
          type: "simple",
          symbol: {
            type: "point-3d",
            symbolLayers: [
              {
                type: "object",
                width: 2,
                height: 2,
                depth: 2,
                resource: { primitive: "sphere" },
                material: { color: this.symbolStartColor },
              },
            ],
          },
        } as any,
        labelsVisible: true,
        labelingInfo: [
          {
            symbol: {
              type: "label-3d",
              symbolLayers: [
                {
                  type: "text",
                  material: {
                    color: "white",
                  },
                  size: 12,
                  halo: {
                    color: "#1c2a48", // autocasts as Color
                    size: 1,
                  },
                },
              ],
              verticalOffset: {
                screenLength: 40,
                maxWorldLength: 100,
                minWorldLength: 20,
              },
            } as any,
            labelExpressionInfo: {
              expression: "$feature.name",
            },
          },
        ],
      });
      this.view.map.add(this.labelFeatureLayer);

      this.animating = false;
      this.animateSymbol();
    }
  }

  public async addPoints(overlayParam: IOverlayParameter) {
    console.time("DiamondPoint");
    this.animating = false;

    const graphics1 = overlayParam.overlays.map((overlay) => {
      const overlayGeometry = overlay.geometry as IPointGeometry;
      const graphic = new this.Graphic({
        geometry: {
          type: "point",
          x: overlayGeometry.x,
          y: overlayGeometry.y,
          z: 0,
        } as any,
        attributes: { ...overlay.fields },
      });
      return graphic;
    });
    const edits1 = { addFeatures: graphics1 };
    await this.animateFeatureLayre.applyEdits(edits1);

    const graphics2 = overlayParam.overlays.map((overlay) => {
      const overlayGeometry = overlay.geometry as IPointGeometry;
      const graphic = new this.Graphic({
        geometry: {
          type: "point",
          x: overlayGeometry.x,
          y: overlayGeometry.y,
          z: 0,
        } as any,
        attributes: { ...overlay.fields },
      });
      return graphic;
    });
    const edits2 = { addFeatures: graphics2 };
    await this.labelFeatureLayer.applyEdits(edits2);

    this.animating = true;
    console.timeEnd("DiamondPoint");
  }

  public async removeAll() {
    this.animating = false;

    let allFeatures = await this.animateFeatureLayre.queryFeatures();
    let edits = { deleteFeatures: allFeatures.features };
    await this.animateFeatureLayre.applyEdits(edits);

    allFeatures = await this.labelFeatureLayer.queryFeatures();
    edits = { deleteFeatures: allFeatures.features };
    await this.labelFeatureLayer.applyEdits(edits);

    this.symbolHeading = 0;
    this.pointHeight = this.pointMinHeight;
  }

  private animateSymbol() {
    if (this.animating) {
      if (this.symbolHeading >= 360) {
        this.symbolHeading = 0;
      }
      this.symbolHeading += 4;

      if (this.pointHeight > this.pointMaxHeight - this.animateSpeed * 2) {
        this.isAscending = false;
      } else if (
        this.pointHeight <
        this.pointMinHeight + this.animateSpeed * 2
      ) {
        this.isAscending = true;
      }

      if (this.isAscending) {
        this.pointHeight += this.animateSpeed;
      } else {
        this.pointHeight -= this.animateSpeed;
      }
      const color = this.Color.blendColors(
        this.symbolStartColor,
        this.symbolEndColor,
        (this.pointHeight - this.pointMinHeight) /
          (this.pointMaxHeight - this.pointMinHeight)
      );

      this.animateFeatureLayre.renderer = {
        type: "simple",
        symbol: {
          type: "point-3d",
          symbolLayers: [
            {
              type: "object",
              width: 5,
              height: (this.pointMaxHeight - 0.5) * this.symbolHeight,
              depth: 5,
              resource: { primitive: "cylinder" },
              material: { color: [255, 255, 255, 0.8] },
            },
            {
              type: "object",
              width: this.symbolWidth,
              height: this.symbolHeight,
              depth: this.symbolWidth,
              anchor: "relative",
              anchorPosition: {
                x: 0,
                y: 0,
                z: this.pointHeight,
              },
              tilt: 180,
              heading: this.symbolHeading,
              resource: { primitive: "diamond" },
              material: { color },
            },
          ],
        },
      } as any;
    }

    setTimeout(() => {
      requestAnimationFrame(() => {
        this.animateSymbol();
      });
    }, 1000 / 20);
  }
}
